import React, { useState, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Trash2,
  Video,
  Clock,
  X,
  Upload,
  FileMusic,
  Search,
} from "lucide-react";
import { useSongLibrary } from "../hooks/useSongLibrary";
import { BordonaImportLibraryModal } from "../components/songs/BordonaImportLibraryModal";
import { useBordonaAccount } from "../hooks/useBordonaAccount";
import { TaskCreatorButton } from "../components/TaskCreatorButton";
import { serializeSongState } from "../lib/taskEngine";
import { StudioLayout } from "../components/layout/StudioLayout";
import { HOME_THEME as T } from "../lib/homeTheme";
import type { SongEntry } from "../hooks/useSongLibrary";
import {
  extractSongLibraryMeta,
  compareSongs,
  SONG_SORT_OPTIONS,
  type SongLibraryMeta,
  type SongSortKey,
} from "../lib/songLibraryMeta";

function DifficultyDots({ level }: { level: 1 | 2 | 3 }) {
  return (
    <div className="flex gap-1 items-center" aria-label={`Dificultad ${level} de 3`}>
      {[1, 2, 3].map((n) => (
        <span
          key={n}
          className={`w-1.5 h-1.5 rounded-full transition-colors ${
            n <= level ? "bg-white/60" : "bg-white/10"
          }`}
        />
      ))}
    </div>
  );
}

const C = {
  lavender: "#c4b5fd",
  lavenderHover: "#e9e0ff",
  bgElevated: "#1c1a19",
};

/* ─── Galería tipográfica — ítem de obra ─── */
const SongGalleryItem = React.forwardRef<
  HTMLDivElement,
  {
    song: SongEntry;
    meta: SongLibraryMeta;
    onPlay: () => void;
    onDelete: () => void;
    showTaskLink?: boolean;
  }
>(({ song, meta, onPlay, onDelete, showTaskLink }, ref) => {
  const tonalityClass =
    meta.tonalityMode === "minor"
      ? "text-[#c4b5fd]/10 group-hover:text-[#c4b5fd]/20"
      : "text-emerald-300/10 group-hover:text-emerald-300/20";

  return (
    <motion.div
      ref={ref}
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      role="button"
      tabIndex={0}
      onClick={onPlay}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onPlay();
        }
      }}
      className="group relative flex flex-col p-6 rounded-lg border border-transparent hover:border-white/5 hover:bg-white/[0.02] transition-all cursor-pointer overflow-hidden min-h-[11rem]"
    >
      <span
        className={`pointer-events-none absolute top-4 right-4 text-4xl font-serif font-light transition-colors select-none ${tonalityClass}`}
        aria-hidden
      >
        {meta.tonality}
      </span>

      <div
        className="h-[1px] w-6 bg-white/20 group-hover:w-16 group-hover:bg-emerald-400 transition-all duration-700 ease-in-out mb-4"
        aria-hidden
      />

      <div className="relative flex items-start gap-3 pr-14 min-w-0">
        <div className="min-w-0 flex-1">
          <h3 className="text-3xl font-serif text-white/90 group-hover:text-white transition-colors truncate leading-tight">
            {song.title}
          </h3>
          <p className="mt-2 text-xs font-sans text-white/60 uppercase tracking-widest truncate">
            {song.artist || "Artista desconocido"}
          </p>
          {song.tempo ? (
            <span className="mt-3 inline-flex items-center gap-1 text-[10px] font-sans text-white/45 tracking-[0.12em] uppercase">
              <Clock className="w-3 h-3" />
              {song.tempo} BPM
            </span>
          ) : null}
        </div>
        {song.youtubeUrl ? (
          <Video className="w-4 h-4 shrink-0 text-white/30 group-hover:text-white/50 transition-colors relative z-[1]" />
        ) : null}
      </div>

      <div className="mt-auto flex flex-col gap-3 w-full pt-4 border-t border-white/5">
        <div className="flex items-center justify-between w-full gap-3">
          <span className="text-[9px] border border-white/10 px-2 py-1 rounded-sm text-white/50 tracking-widest uppercase font-sans shrink-0">
            {meta.format}
          </span>
          <DifficultyDots level={meta.difficulty} />
        </div>

        {showTaskLink && song.musicXml ? (
          <div
            className="opacity-0 group-hover:opacity-100 transition-all transform translate-y-2 group-hover:translate-y-0"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <TaskCreatorButton
              type="song"
              title={song.title}
              description={
                song.artist
                  ? `${song.title} — ${song.artist}`
                  : `Pieza MusicXML: ${song.title}`
              }
              config={serializeSongState(song.title, song.musicXml, {
                youtubeUrl: song.youtubeUrl,
                tempo: song.tempo,
              })}
              variant="serenity"
            />
          </div>
        ) : null}
      </div>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="absolute top-4 right-4 z-10 p-1.5 opacity-0 group-hover:opacity-100 text-white/25 hover:text-white/60 transition-opacity"
        title="Eliminar"
        aria-label={`Eliminar ${song.title}`}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </motion.div>
  );
});
SongGalleryItem.displayName = "SongGalleryItem";

/* ─── Upload Modal ─── */
const UploadModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onUpload: (data: { title: string; artist: string; xml: string; youtubeUrl: string }) => void;
}> = ({ open, onClose, onUpload }) => {
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [xmlContent, setXmlContent] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback((file: File) => {
    if (!file.name.endsWith(".xml") && !file.name.endsWith(".musicxml")) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setXmlContent(text);
      const titleMatch = text.match(/<work-title>([^<]+)<\/work-title>/);
      const composerMatch = text.match(/<creator[^>]*>([^<]+)<\/creator>/);
      if (titleMatch) setTitle(titleMatch[1]);
      if (composerMatch) setArtist(composerMatch[1]);
    };
    reader.readAsText(file);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const canSubmit = title && xmlContent;

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 16 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-lg rounded-2xl overflow-hidden border border-white/10"
          style={{ backgroundColor: C.bgElevated }}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
            <h2 className="text-lg font-serif text-white/90">Nueva Canción</h2>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/5 transition-colors"
            >
              <X className="w-4 h-4 text-white/50" />
            </button>
          </div>

          <div className="p-6 space-y-5">
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`relative rounded-xl border-2 border-dashed cursor-pointer transition-all ${
                dragOver ? "border-[#c4b5fd]/60 bg-[#c4b5fd]/5" : "border-white/15"
              } ${xmlContent ? "p-5" : "py-10"}`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xml,.musicxml"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFile(file);
                }}
              />
              {xmlContent ? (
                <div className="flex items-center gap-3">
                  <FileMusic className="w-5 h-5 text-[#c4b5fd]" />
                  <span className="text-sm text-white/50">
                    Archivo cargado ({xmlContent.length.toLocaleString()} chars)
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setXmlContent("");
                    }}
                    className="ml-auto"
                  >
                    <X className="w-4 h-4 text-white/30" />
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-white/30" />
                  <p className="text-sm text-white/50">
                    Arrastra un MusicXML o haz clic para seleccionar
                  </p>
                  <p className="text-xs mt-1 text-white/30">.xml, .musicxml</p>
                </div>
              )}
            </div>

            <div className="space-y-3">
              {(
                [
                  ["Título", title, setTitle, "Nombre de la canción"],
                  ["Artista", artist, setArtist, "Artista o compositor"],
                  ["YouTube (opcional)", youtubeUrl, setYoutubeUrl, "https://youtube.com/watch?v=..."],
                ] as const
              ).map(([label, value, setter, placeholder]) => (
                <div key={label}>
                  <label className="text-xs uppercase tracking-wider font-mono text-white/35 mb-1.5 block">
                    {label}
                  </label>
                  <input
                    value={value}
                    onChange={(e) => setter(e.target.value)}
                    placeholder={placeholder}
                    className="w-full px-3.5 py-2.5 rounded-lg text-sm outline-none bg-black/30 border border-white/10 text-white/90 focus:border-[#c4b5fd]/40 transition-colors"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-white/50 hover:text-white/80 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => {
                onUpload({ title, artist, xml: xmlContent, youtubeUrl });
                onClose();
                setTitle("");
                setArtist("");
                setXmlContent("");
                setYoutubeUrl("");
              }}
              disabled={!canSubmit}
              className="px-5 py-2 text-sm text-[#c4b5fd] hover:text-[#e9e0ff] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Guardar y practicar →
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/* ─── Main Library Page ─── */
export const SongLibraryPage: React.FC = () => {
  const navigate = useNavigate();
  const { isTeacher } = useBordonaAccount();
  const { items: songs, importableItems, loading, saveSong, deleteSong, importToBordona } =
    useSongLibrary();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importingId, setImportingId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SongSortKey>("recent");

  const displayed = useMemo(() => {
    const q = search.trim().toLowerCase();
    const withMeta = songs
      .filter(
        (s) =>
          !q ||
          s.title.toLowerCase().includes(q) ||
          (s.artist || "").toLowerCase().includes(q)
      )
      .map((s) => ({
        ...s,
        meta: extractSongLibraryMeta(s.musicXml),
      }));
    return [...withMeta].sort((a, b) => compareSongs(a, b, sortKey));
  }, [songs, search, sortKey]);

  const handlePlay = (songId: string) => {
    navigate(`/songs/${songId}`);
  };

  const handleUpload = async (data: {
    title: string;
    artist: string;
    xml: string;
    youtubeUrl: string;
  }) => {
    const id = await saveSong({
      title: data.title,
      artist: data.artist,
      musicXml: data.xml,
      youtubeUrl: data.youtubeUrl || undefined,
    });
    if (id) {
      navigate(`/songs/${id}`);
    }
  };

  return (
    <StudioLayout>
      <div className="w-full px-12 pt-28 pb-24 min-h-full flex flex-col">
        <header>
          <div className="flex flex-wrap items-center justify-between gap-8">
            <div className="min-w-0 flex-1">
              <p
                className="text-xs tracking-widest uppercase font-sans"
                style={{ color: "rgba(153, 246, 228, 0.5)" }}
              >
                Repertorio
              </p>
              <h1
                className="mt-4 font-serif font-light tracking-tight text-[clamp(2.5rem,6vw,4rem)] leading-[0.95]"
                style={{ color: T.text }}
              >
                Biblioteca
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-white/60 font-sans font-light">
                {songs.length} {songs.length === 1 ? "obra" : "obras"} en tu colección
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-6 md:gap-8 shrink-0">
              <div className="flex items-center gap-2 border-b border-white/10 pb-1">
                <Search className="w-4 h-4 text-white/30" strokeWidth={1.5} />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar obra o artista…"
                  className="bg-transparent outline-none text-sm w-40 md:w-44 text-white/70 placeholder:text-white/30 font-sans font-light"
                />
              </div>

              <label className="flex items-center gap-2 text-xs text-white/50 font-mono uppercase tracking-widest cursor-pointer hover:text-white/80 transition-colors">
                <span className="hidden sm:inline">Ordenar</span>
                <select
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value as SongSortKey)}
                  className="bg-transparent border-none outline-none cursor-pointer text-white/50 hover:text-white/80 uppercase tracking-widest max-w-[9rem]"
                  aria-label="Ordenar biblioteca"
                >
                  {SONG_SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value} style={{ background: T.bg, color: T.text }}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <span className="text-white/30" aria-hidden>
                  ↓
                </span>
              </label>

              <button
                type="button"
                onClick={() => setImportOpen(true)}
                className="text-white/50 hover:text-white/80 transition-colors text-[11px] font-sans tracking-[0.14em] uppercase"
              >
                Importar{importableItems.length > 0 ? ` (${importableItems.length})` : ""}
              </button>

              <button
                type="button"
                onClick={() => setUploadOpen(true)}
                className="text-[#c4b5fd] hover:text-[#e9e0ff] transition-colors flex items-center gap-2 text-[11px] font-sans tracking-[0.14em] uppercase"
              >
                <Plus className="w-4 h-4" strokeWidth={1.5} />
                Nueva Canción
              </button>
            </div>
          </div>
        </header>

        <div className="flex-1 min-h-0">
          {isTeacher && (
            <p className="text-xs text-white/50 font-sans tracking-wide mt-12 mb-12 border-b border-white/5 pb-4 leading-relaxed font-light">
              Sube una canción MusicXML con «Nueva Canción», luego usa{" "}
              <span className="text-white/55">Crear tarea pedagógica</span> al pasar el cursor sobre
              una obra para generar el código de 6 caracteres. Opcional: restringe la tarea a una
              clase.
            </p>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-8 h-8 rounded-full border-2 border-[#c4b5fd]/40 border-t-transparent animate-spin" />
            </div>
          ) : displayed.length === 0 ? (
            <div className="flex flex-col items-start justify-center gap-4 max-w-md py-16">
              <div
                className="h-[1px] w-6 bg-white/20 mb-4"
                aria-hidden
              />
              <p className="text-sm text-white/50 font-sans font-light leading-relaxed">
                {search ? "Sin resultados para tu búsqueda." : "Tu biblioteca está vacía."}
              </p>
              {!search && (
                <button
                  type="button"
                  onClick={() => setUploadOpen(true)}
                  className="text-[11px] font-sans tracking-[0.14em] uppercase text-[#c4b5fd] hover:text-[#e9e0ff] transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" strokeWidth={1.5} />
                  Cargar tu primera canción
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-12 gap-y-16 mt-12">
              <AnimatePresence mode="popLayout">
                {displayed.map((song) => (
                  <SongGalleryItem
                    key={song.id}
                    song={song}
                    meta={song.meta}
                    onPlay={() => handlePlay(song.id)}
                    onDelete={() => deleteSong(song.id)}
                    showTaskLink={isTeacher}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>

        <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} onUpload={handleUpload} />
        <BordonaImportLibraryModal
          open={importOpen}
          onClose={() => setImportOpen(false)}
          songs={importableItems}
          importingId={importingId}
          onImport={async (id) => {
            setImportingId(id);
            await importToBordona(id);
            setImportingId(null);
            navigate(`/songs/${id}`);
          }}
        />
      </div>
    </StudioLayout>
  );
};

export default SongLibraryPage;
